import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useServices } from "../../hooks/useServices";
import { useAuth } from "../../hooks/useAuth";
import { V2, pickLang } from "../copy";
import { Arrow, Display, Eyebrow } from "./Primitives";
import { route } from "../paths";

const fmt = (n: number, lang: "fr" | "en") => {
  const v = Number(n);
  if (Number.isNaN(v)) return "";
  const s = v % 1 === 0 ? String(v) : v.toFixed(2);
  return lang === "fr" ? s.replace(".", ",") : s;
};

/** Live pricing from the API, laid out as a hairline grid of "plates". */
const PricingV2: React.FC<{ hideHeader?: boolean }> = ({ hideHeader = false }) => {
  const { currentLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const c = V2[lang].pricing;
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { services, loading } = useServices({ autoFetch: true });

  // Same ordering rule as v1: kids' service last.
  const plans = [...services].sort((a, b) => {
    const ak = /enfant|kids/i.test(a.name) ? 1 : 0;
    const bk = /enfant|kids/i.test(b.name) ? 1 : 0;
    return ak - bk;
  });

  const choose = (id: string) => {
    navigate(
      isAuthenticated
        ? `/${lang}/dashboard?tab=booking&service=${id}`
        : `/${lang}/login?redirect=dashboard&service=${id}`,
    );
  };

  return (
    <section id="v2-pricing" className={`bg-ink text-bone ${hideHeader ? "py-16 sm:py-24" : "v2-hair border-t py-24 sm:py-32"}`}>
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6">
        {!hideHeader && (
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow index="06">{c.label}</Eyebrow>
            <Display className="mt-5 text-[clamp(2.6rem,7vw,6.5rem)]">
              {c.title}
            </Display>
          </div>
          <div className="v2-reveal max-w-sm" data-delay="2">
            <p className="text-bone/60">{c.subtitle}</p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <Link to={route(lang, "tarifs")} className="v2-link inline-flex items-center gap-2">
                {c.subs}
                <Arrow />
              </Link>
              <Link to={route(lang, "sur-mesure")} className="v2-link inline-flex items-center gap-2">
                {c.custom}
                <Arrow />
              </Link>
            </div>
          </div>
        </div>
        )}

        <div className={`grid gap-px border border-bone/10 bg-bone/10 md:grid-cols-2 lg:grid-cols-3 ${hideHeader ? "" : "mt-14"}`}>
          {loading && plans.length === 0
            ? Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-[520px] animate-pulse bg-ink" />
              ))
            : plans.map((p, i) => (
                <article
                  key={p.id}
                  className={`v2-reveal relative flex flex-col p-7 sm:p-9 ${
                    p.popular ? "bg-[#141416]" : "bg-ink"
                  }`}
                  data-delay={String((i % 3) + 1)}
                >
                  {p.popular && (
                    <span className="absolute right-6 top-6 rounded-full bg-tangerine px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white">
                      {c.popular}
                    </span>
                  )}

                  <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-teal-light/80">
                    {p.category}
                  </div>
                  <h3 className="mt-5 font-display text-3xl leading-tight sm:text-4xl">
                    {p.name}
                  </h3>

                  <div className="mt-6 flex items-baseline gap-3">
                    <span className="font-display text-6xl leading-none sm:text-7xl">
                      {fmt(p.price, lang)}
                      <span className="text-4xl">€</span>
                    </span>
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bone/50">
                      {c.perPair}
                    </span>
                    {p.originalPrice && Number(p.originalPrice) > Number(p.price) && (
                      <span className="font-mono text-sm text-bone/35 line-through">
                        {fmt(p.originalPrice, lang)}€
                      </span>
                    )}
                  </div>

                  <p className="mt-4 text-sm leading-relaxed text-bone/60">
                    {p.shortDescription}
                  </p>

                  <ul className="mt-7 space-y-2.5 text-sm">
                    {p.features?.map((f, k) => (
                      <li key={k} className="flex gap-3">
                        <span className="mt-[9px] h-px w-3 flex-shrink-0 bg-teal" />
                        <span className="text-bone/85">{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-9">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-bone/10 pt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-bone/50">
                      <span>
                        {`${c.duration} · ${p.durationText}`}
                      </span>
                      {p.includesPickup && <span className="text-tangerine">{c.pickup}</span>}
                    </div>
                    <button
                      onClick={() => choose(p.id)}
                      className="v2-btn v2-btn-solid mt-5 w-full justify-center"
                    >
                      {c.choose}
                      <Arrow />
                    </button>
                  </div>
                </article>
              ))}
        </div>
      </div>
    </section>
  );
};

export default PricingV2;
